import React from 'react';
import styled from 'styled-components';
import PropertyMap from './PropertyMap';

const HeaderContainer = styled.div`
  margin-bottom: 30px;
`;

const PropertyTitle = styled.h1`
  color: #333;
  margin: 0 0 8px 0;
  font-size: 24px;
  line-height: 1.3;
  
  @media (min-width: 768px) {
    font-size: 28px;
    margin: 0 0 12px 0;
  }
`;

const PropertySubtitle = styled.p`
  color: #666;
  margin: 0 0 20px 0;
  font-size: 16px;
  
  @media (min-width: 768px) {
    font-size: 18px;
    margin: 0 0 24px 0;
  }
`;

const MediaGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
  margin-bottom: 20px;
  
  @media (min-width: 768px) {
    grid-template-columns: 1fr 1fr;
    gap: 20px;
    margin-bottom: 24px;
  }
`;

const PhotoContainer = styled.div`
  position: relative;
  border-radius: 12px;
  overflow: hidden;
  background: #f0f0f0;
  aspect-ratio: 4/3;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
  
  .photo-placeholder {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background: linear-gradient(45deg, #f8f9fa, #e9ecef);
    color: #666;
    text-align: center;
    padding: 20px;
    
    .placeholder-icon {
      font-size: 48px;
      margin-bottom: 12px;
    }
    
    .placeholder-text {
      font-size: 16px;
      font-weight: 500;
      margin-bottom: 8px;
    }
    
    .placeholder-hint {
      font-size: 14px;
      color: #999;
    }
  }
  
  .photo-overlay {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    background: linear-gradient(transparent, rgba(0,0,0,0.7));
    color: white;
    padding: 20px 16px 16px;
    
    .overlay-title {
      font-size: 14px;
      font-weight: 600;
      margin-bottom: 4px;
    }
    
    .overlay-address {
      font-size: 12px;
      opacity: 0.9;
    }
  }
`;

const MapContainer = styled.div`
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
`;

const PropertyInfo = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  background: #f8f9fa;
  border-radius: 12px;
  padding: 20px;
  border: 1px solid #e9ecef;
  
  @media (min-width: 768px) {
    gap: 24px;
    padding: 24px;
  }
`;

const InfoItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  
  .info-icon {
    font-size: 18px;
    width: 24px;
    text-align: center;
  }
  
  .info-content {
    .info-label {
      font-size: 12px;
      color: #666;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      margin-bottom: 2px;
    }
    
    .info-value {
      font-size: 14px;
      font-weight: 600;
      color: #333;
    }
  }
`;

const PropertyHeader = ({ property, showTitle = true, showInfo = true }) => {
  const getDisplayAddress = () => {
    if (!property) return '';
    
    const components = [
      property.address_line1,
      property.unit_number,
      property.city,
      property.state,
      property.zip_code
    ].filter(Boolean);
    
    return components.join(', ');
  };

  const getShortAddress = () => {
    if (!property) return '';
    
    const address = property.address_line1;
    const unit = property.unit_number;
    
    return unit ? `${address}, ${unit}` : address;
  };

  const getCityState = () => {
    if (!property) return '';
    return `${property.city}, ${property.state} ${property.zip_code}`;
  };

  const formatRent = (amount) => {
    if (!amount) return 'Rent TBD';
    return `$${parseInt(amount).toLocaleString()}/month`;
  };

  const formatBedsBaths = () => {
    const beds = property?.bedrooms || 0;
    const baths = property?.bathrooms || 0;
    
    if (beds && baths) {
      return `${beds} bed, ${baths} bath`;
    } else if (beds) {
      return `${beds} bedroom`;
    } else if (baths) {
      return `${baths} bathroom`;
    }
    return 'Studio/Other';
  };

  const formatSquareFeet = () => {
    const sqft = property?.square_feet;
    if (sqft) {
      return `${parseInt(sqft).toLocaleString()} sq ft`;
    }
    return null;
  };

  return (
    <HeaderContainer>
      {showTitle && (
        <>
          <PropertyTitle>
            🏠 {getShortAddress()}
          </PropertyTitle>
          <PropertySubtitle>
            {getCityState()}
          </PropertySubtitle>
        </>
      )}
      
      <MapContainer>
        <PropertyMap 
          property={property} 
          height="400px" 
          showOverlay={true}
        />
      </MapContainer>
      
    </HeaderContainer>
  );
};

export default PropertyHeader;